import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

export default function BatchManagementPage() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "batches";

    /* ================= COMMON ================= */
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }),
        [token]
    );





    /* ================= EXCEL UPLOAD ================= */
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select batch Excel file");
        let dataRows;
        let headerRow;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            headerRow = raw[0];
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = [headerRow, ...dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE)];
            const res = await fetch(`${BASE_URL}/api/batches/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllBatches();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    /* ================= TABLE ================= */
    const [batches, setBatches] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [totalCount, setTotalCount] = useState(null);
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setBatches(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${BASE_URL}/api/batches?page=0&size=100`, { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setBatches(all);
            setAllLoaded(false);
            store.setRows(CACHE_KEY, all, false);
        } catch {
            setBatches([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllBatches = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(`${BASE_URL}/api/batches`, { headers: authHeaders });
            const data = await res.json();
            const all = Array.isArray(data) ? data : [];
            setBatches(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
            store.markAllLoaded(CACHE_KEY, all);
        } catch {
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => {
        fetchFirst100();
    }, []);

    useEffect(() => {
        fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d) setTotalCount(d.batches ?? null); })
            .catch(() => { });
    }, []);

    useEffect(() => { setVisibleCount(100); }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return batches.filter(
            (b) =>
                b.epBatchId?.toLowerCase().includes(q) ||
                b.batchName?.toLowerCase().includes(q) ||
                b.projectId?.toLowerCase().includes(q) ||
                b.sectorName?.toLowerCase().includes(q) ||
                b.jobRoleName?.toLowerCase().includes(q)
        );
    }, [batches, search]);

    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = batches;
            if (!allLoaded) {
                const res = await fetch(`${BASE_URL}/api/batches`, { headers: authHeaders });
                const json = await res.json();
                const all = Array.isArray(json) ? json : (json?.content ?? []);
                data = all;
                setBatches(all);
                setAllLoaded(true);
                setVisibleCount(all.length);
                store.setRows(CACHE_KEY, all, true);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Batches");
            XLSX.writeFile(wb, "batches.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    /* ================= EDIT ================= */
    const emptyForm = {
        epBatchId: "",
        fy: "",
        batchCreationDate: "",
        scheme: "",
        projectId: "",
        projectImplementationType: "",
        sectorName: "",
        jobRoleName: "",
        associatedQpCode: "",
        nsqfLevel: "",
        trainingHoursPerDay: "",
        batchStartTime: "",
        batchEndTime: "",
        batchName: "",
        batchStartDate: "",
        batchEndDate: "",
        batchType: "",
        batchSize: "",
        assessmentMode: "",
        proposedAssessmentDate: "",
        trainingFee: "",
        feesPaidBy: "",
        feesPaidByName: "",
        sidhBatchId: "",
    };

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const openEdit = (row) => {
        setEditingId(row.epBatchId);
        setForm({
            ...emptyForm,
            ...row, // ← THIS is the key
        });
    };
    // const updateBatch = async () => {
    //     try {
    //         await fetch(
    //             `${BASE_URL}/batches?epBatchId=${encodeURIComponent(
    //                 editingId
    //             )}&actor=Rahul`,
    //             {
    //                 method: "PUT",
    //                 headers: jsonHeaders,
    //                 body: JSON.stringify(form),
    //             }
    //         );
    //         flash("ok", "Batch updated");
    //         setEditingId(null);
    //         fetchBatches();
    //     } catch {
    //         flash("err", "Update failed");
    //     }
    // };

    const updateBatch = async () => {
        const payload = {
            batchName: form.batchName,
            batchSize: form.batchSize,
            batchType: form.batchType,
            batchStartDate: form.batchStartDate,
            batchEndDate: form.batchEndDate,
        };

        await fetch(
            `${BASE_URL}/api/batches?epBatchId=${encodeURIComponent(editingId)}&actor=Rahul`,
            {
                method: "PUT",
                headers: jsonHeaders,
                body: JSON.stringify(payload),
            }
        );

        flash("ok", "Batch updated");
        setEditingId(null);
        store.invalidate(CACHE_KEY);
        fetchAllBatches();
    };


    /* ================= DELETE ================= */
    const deleteBatch = async (epBatchId) => {
        if (!window.confirm("Deactivate this batch?")) return;

        await fetch(
            `${BASE_URL}/api/batches?epBatchId=${encodeURIComponent(
                epBatchId
            )}&actor=Rahul`,
            {
                method: "DELETE",
                headers: authHeaders,
            }
        );
        flash("ok", "Batch deactivated");
        store.invalidate(CACHE_KEY);
        fetchAllBatches();
    };

    /* ================= AUDIT ================= */
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditRows, setAuditRows] = useState([]);

    const openAudit = async (epBatchId) => {
        setAuditOpen(true);
        setAuditRows([]);

        try {
            const res = await fetch(
                `${BASE_URL}/api/batches/audits/by-batch?epBatchId=${encodeURIComponent(epBatchId)}`,

                { headers: authHeaders }
            );

            const text = await res.text();   // ✅ read raw text safely
            const data = text ? JSON.parse(text) : [];

            setAuditRows(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Audit fetch failed", err);
            setAuditRows([]);
        }
    };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/batch_template.xlsx"; // local path
        a.download = "batch_template.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };



    /* ================= UI ================= */
    return (
        <div className="cmp-page">
            <h1>Batch Management</h1>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card grid-4">
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }}
                    onChange={(e) => setFile(e.target.files[0])}
                />
                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>
                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Excel Template
                </button>

            </div>

            {/* Table */}
            <div className="card">


                <div className="card-top">
                    <div className="card-title" style={{ fontWeight: "800" }}>2️⃣ Batch Table</div>

                    <div className="table-actions" style={{ marginTop: "1rem" }}>
                        <input
                            className="search"
                            placeholder="Search batch..."
                            style={{ marginRight: "1rem" }}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <button className="btn" onClick={exportExcel} disabled={exporting}>
                            {exporting ? "Processing..." : "Export Excel"}
                        </button>

                    </div>
                </div>
                {totalCount !== null && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
                        Total: {totalCount.toLocaleString()}
                    </div>
                )}




                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>EP Batch ID</th>
                                <th>FY</th>
                                <th>Batch Creation Date</th>
                                <th>Scheme</th>
                                <th>Project ID</th>
                                <th>Project Implementation Type</th>
                                <th>Sector Name</th>
                                <th>Job Role Name</th>
                                <th>Associated QP Code</th>
                                <th>NSQF Level</th>
                                <th>Training Hours / Day</th>
                                <th>Batch Start Time</th>
                                <th>Batch End Time</th>
                                <th>Batch Name</th>
                                <th>Batch Start Date</th>
                                <th>Batch End Date</th>
                                <th>Batch Type</th>
                                <th>Batch Size</th>
                                <th>Assessment Mode</th>
                                <th>Proposed Assessment Date</th>
                                <th>Training Fee</th>
                                <th>Fees Paid By</th>
                                <th>Fees Paid By Name</th>
                                <th>SIDH Batch ID</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {filtered.slice(0, visibleCount).map((b, index) => (
                                <tr key={b.epBatchId}>
                                    <td>{index + 1}</td>
                                    <td>{b.epBatchId}</td>
                                    <td>{b.fy}</td>
                                    <td>{b.batchCreationDate}</td>
                                    <td>{b.scheme}</td>
                                    <td>{b.projectId}</td>
                                    <td>{b.projectImplementationType}</td>
                                    <td>{b.sectorName}</td>
                                    <td>{b.jobRoleName}</td>
                                    <td>{b.associatedQpCode}</td>
                                    <td>{b.nsqfLevel}</td>
                                    <td>{b.trainingHoursPerDay}</td>
                                    <td>{b.batchStartTime}</td>
                                    <td>{b.batchEndTime}</td>
                                    <td>{b.batchName}</td>
                                    <td>{b.batchStartDate}</td>
                                    <td>{b.batchEndDate}</td>
                                    <td>{b.batchType}</td>
                                    <td>{b.batchSize}</td>
                                    <td>{b.assessmentMode}</td>
                                    <td>{b.proposedAssessmentDate}</td>
                                    <td>{b.trainingFee}</td>
                                    <td>{b.feesPaidBy}</td>
                                    <td>{b.feesPaidByName}</td>
                                    <td>{b.sidhBatchId}</td>
                                    <td className="actions">
                                        <button className="btn btn-sm" onClick={() => openEdit(b)}>
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteBatch(b.epBatchId)}
                                        >
                                            Deactivate
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {!filtered.length && (
                                <tr>
                                    <td colSpan="26" className="muted">
                                        {loading ? "Loading..." : "No batches found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan="26" style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllBatches} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan="26" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 100)}
                                        >
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                </div>
            </div>

            {/* EDIT MODAL */}
            {editingId &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setEditingId(null)}>
                        <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                            <h2>Edit Batch — {editingId}</h2>

                            <div className="form-grid">
                                {[
                                    { key: "batchName", label: "Batch Name" },
                                    { key: "batchType", label: "Batch Type" },
                                    { key: "batchSize", label: "Batch Size" },
                                    { key: "batchStartDate", label: "Batch Start Date", type: "date" },
                                    { key: "batchEndDate", label: "Batch End Date", type: "date" },
                                ].map(({ key, label, type = "text" }) => (
                                    <div className="field" key={key}>
                                        <label>{label}</label>
                                        <input
                                            type={type}
                                            value={form[key] ?? ""}
                                            onChange={(e) =>
                                                setForm((p) => ({ ...p, [key]: e.target.value }))
                                            }
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="modal-actions">
                                <button className="btn" onClick={() => setEditingId(null)}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" onClick={updateBatch}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}


            {/* AUDIT MODAL */}
            {auditOpen &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
                        <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
                            <h2>Batch Audit Logs</h2>
                            {/* <pre>{JSON.stringify(auditRows, null, 2)}</pre> */}
                            {auditRows.length === 0 ? (
                                <div className="muted">No audit logs found</div>
                            ) : (
                                <pre>{JSON.stringify(auditRows, null, 2)}</pre>
                            )}

                        </div>
                    </div>,
                    document.body
                )}

            {/* ================= INLINE CSS ================= */}
            <style>{`
            .form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
}

.field input {
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.ep-modal.wide {
  width: 1100px;
  max-height: 85vh;
  overflow: auto;
}

        .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
        h1 { font-size:26px; font-weight:900 }

        .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
        .flash-ok { background:#ecfdf5; color:#065f46 }
        .flash-err { background:#fef2f2; color:#991b1b }

        .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
        .grid-4 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

        .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
        .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
        .btn-sm { padding:6px 10px; font-size:12px }
        .btn-danger { color:#991b1b; border-color:#fca5a5 }

        .search { padding:10px; border-radius:12px; border:1px solid #e5e7eb; margin-bottom:10px }

        .table-wrap { overflow-x:auto }
        .ep-table { width:100%; min-width:800px; border-collapse:collapse }
        th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
        th { color:#6b7280; text-align:left }

        .actions { display:flex; gap:8px }
        .muted { color:#6b7280; font-weight:700 }

        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
        .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
        .ep-modal.wide { width:900px }
        .ep-modal input { width:100%; margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

        .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
      `}</style>
        </div>
    );
}


// import React, { useEffect, useMemo, useState } from "react";
// import { createPortal } from "react-dom";
// import * as XLSX from "xlsx";
// import { filterRowsForCurrentScope } from "../../utils/centerScope";
// import useDataStore from "../../store/dataStore";

// const BASE_URL = "https://mis.empowerplus.in";

// export default function BatchManagementPage() {
//     const token = localStorage.getItem("token");
//     const store = useDataStore();
//     const CACHE_KEY = "batches";

//     /* ================= COMMON ================= */
//     const [msg, setMsg] = useState({ type: "", text: "" });
//     const flash = (type, text) => {
//         setMsg({ type, text });
//     };

//     const authHeaders = useMemo(
//         () => ({ Authorization: `Bearer ${token}` }),
//         [token]
//     );

//     const jsonHeaders = useMemo(
//         () => ({
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//         }),
//         [token]
//     );





//     /* ================= EXCEL UPLOAD ================= */
//     const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [uploadProgress, setUploadProgress] = useState(null);

//     const CHUNK_SIZE = 5000;
//     const PARALLEL = 6;

//     const uploadExcel = async () => {
//         if (!file) return flash("err", "Select batch Excel file");
//         let dataRows;
//         try {
//             const buffer = await file.arrayBuffer();
//             const wb = XLSX.read(buffer, { type: "array" });
//             const ws = wb.Sheets[wb.SheetNames[0]];
//             const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
//             dataRows = raw.slice(1);
//         } catch (err) {
//             return flash("err", `Cannot read file: ${err.message}`);
//         }
//         if (!dataRows.length) return flash("err", "Excel file is empty");
//         const totalRows = dataRows.length;
//         const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
//         setUploading(true);
//         setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
//         let uploadedSoFar = 0;
//         let totalInserted = 0;
//         let totalSkipped = 0;
//         let failed = false;
//         let firstError = "";
//         const uploadOneChunk = async (i) => {
//             const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
//             const res = await fetch(`${BASE_URL}/api/batches/upload-json?actor=Rahul`, {
//                 method: "POST",
//                 headers: { ...authHeaders, "Content-Type": "application/json" },
//                 body: JSON.stringify(chunkRows),
//             });
//             if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
//             const text = await res.text();
//             let data;
//             try { data = JSON.parse(text); } catch { data = text; }
//             if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
//             return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
//         };
//         for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
//             const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
//             const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
//             const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
//             for (const result of results) {
//                 if (result.status === "fulfilled") {
//                     totalInserted += result.value.inserted;
//                     totalSkipped += result.value.skipped;
//                     uploadedSoFar += result.value.inserted + result.value.skipped;
//                 } else {
//                     failed = true;
//                     firstError = result.reason?.message || "Upload failed";
//                 }
//             }
//             setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
//         }
//         setUploading(false);
//         if (!failed) {
//             flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
//             setFile(null);
//             setUploadProgress(null);
//             store.invalidate(CACHE_KEY);
//             fetchAllBatches();
//         } else {
//             flash("err", firstError);
//             setUploadProgress(prev => prev ? { ...prev, error: true } : null);
//         }
//     };

//     /* ================= TABLE ================= */
//     const [batches, setBatches] = useState(() => store.getRows(CACHE_KEY));
//     const [loading, setLoading] = useState(false);
//     const [loadingAll, setLoadingAll] = useState(false);
//     const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
//     const [totalCount, setTotalCount] = useState(null);
//     const [search, setSearch] = useState("");
//     const [visibleCount, setVisibleCount] = useState(100);

//     const fetchFirst100 = async () => {
//         if (store.isCached(CACHE_KEY)) {
//             setBatches(store.getRows(CACHE_KEY));
//             setAllLoaded(store.isAllLoaded(CACHE_KEY));
//             return;
//         }
//         setLoading(true);
//         try {
//             const res = await fetch(`${BASE_URL}/api/batches`, { headers: authHeaders });
//             const data = await res.json();
//             const all = Array.isArray(data) ? data : [];
//             setBatches(all);
//             setAllLoaded(true);
//             store.setRows(CACHE_KEY, all, true);
//         } catch {
//             setBatches([]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const fetchAllBatches = async () => {
//         setLoadingAll(true);
//         try {
//             const res = await fetch(`${BASE_URL}/api/batches`, { headers: authHeaders });
//             const data = await res.json();
//             const all = Array.isArray(data) ? data : [];
//             setBatches(all);
//             setAllLoaded(true);
//             setVisibleCount(all.length);
//             store.markAllLoaded(CACHE_KEY, all);
//         } catch {
//         } finally {
//             setLoadingAll(false);
//         }
//     };

//     useEffect(() => {
//         fetchFirst100();
//     }, []);

//     useEffect(() => {
//         fetch(`${BASE_URL}/api/dashboard/counts`, { headers: authHeaders })
//             .then(r => r.ok ? r.json() : null)
//             .then(d => { if (d) setTotalCount(d.batches ?? null); })
//             .catch(() => {});
//     }, []);

//     useEffect(() => { setVisibleCount(100); }, [search]);

//     const scopedBatches = useMemo(() => filterRowsForCurrentScope(batches), [batches]);

//     const filtered = useMemo(() => {
//         const q = search.toLowerCase();
//         return scopedBatches.filter(
//             (b) =>
//                 b.epBatchId?.toLowerCase().includes(q) ||
//                 b.batchName?.toLowerCase().includes(q) ||
//                 b.projectId?.toLowerCase().includes(q) ||
//                 b.sectorName?.toLowerCase().includes(q) ||
//                 b.jobRoleName?.toLowerCase().includes(q)
//         );
//     }, [scopedBatches, search]);

//     const [exporting, setExporting] = useState(false);

//     const exportExcel = () => {
//         if (exporting) return;
//         setExporting(true);
//         setTimeout(() => {
//             try {
//                 const ws = XLSX.utils.json_to_sheet(filtered);
//                 const wb = XLSX.utils.book_new();
//                 XLSX.utils.book_append_sheet(wb, ws, "Batches");
//                 XLSX.writeFile(wb, "batches.xlsx");
//             } finally {
//                 setExporting(false);
//             }
//         }, 50);
//     };

//     /* ================= EDIT ================= */
//     const emptyForm = {
//         epBatchId: "",
//         fy: "",
//         batchCreationDate: "",
//         scheme: "",
//         projectId: "",
//         projectImplementationType: "",
//         sectorName: "",
//         jobRoleName: "",
//         associatedQpCode: "",
//         nsqfLevel: "",
//         trainingHoursPerDay: "",
//         batchStartTime: "",
//         batchEndTime: "",
//         batchName: "",
//         batchStartDate: "",
//         batchEndDate: "",
//         batchType: "",
//         batchSize: "",
//         assessmentMode: "",
//         proposedAssessmentDate: "",
//         trainingFee: "",
//         feesPaidBy: "",
//         feesPaidByName: "",
//         sidhBatchId: "",
//     };

//     const [editingId, setEditingId] = useState(null);
//     const [form, setForm] = useState(emptyForm);
//     const openEdit = (row) => {
//         setEditingId(row.epBatchId);
//         setForm({
//             ...emptyForm,
//             ...row, // ← THIS is the key
//         });
//     };
//     // const updateBatch = async () => {
//     //     try {
//     //         await fetch(
//     //             `${BASE_URL}/batches?epBatchId=${encodeURIComponent(
//     //                 editingId
//     //             )}&actor=Rahul`,
//     //             {
//     //                 method: "PUT",
//     //                 headers: jsonHeaders,
//     //                 body: JSON.stringify(form),
//     //             }
//     //         );
//     //         flash("ok", "Batch updated");
//     //         setEditingId(null);
//     //         fetchBatches();
//     //     } catch {
//     //         flash("err", "Update failed");
//     //     }
//     // };

//     const updateBatch = async () => {
//         const payload = {
//             batchName: form.batchName,
//             batchSize: form.batchSize,
//             batchType: form.batchType,
//             batchStartDate: form.batchStartDate,
//             batchEndDate: form.batchEndDate,
//         };

//         await fetch(
//             `${BASE_URL}/api/batches?epBatchId=${encodeURIComponent(editingId)}&actor=Rahul`,
//             {
//                 method: "PUT",
//                 headers: jsonHeaders,
//                 body: JSON.stringify(payload),
//             }
//         );

//         flash("ok", "Batch updated");
//         setEditingId(null);
//         store.invalidate(CACHE_KEY);
//         fetchAllBatches();
//     };


//     /* ================= DELETE ================= */
//     const deleteBatch = async (epBatchId) => {
//         if (!window.confirm("Deactivate this batch?")) return;

//         await fetch(
//             `${BASE_URL}/api/batches?epBatchId=${encodeURIComponent(
//                 epBatchId
//             )}&actor=Rahul`,
//             {
//                 method: "DELETE",
//                 headers: authHeaders,
//             }
//         );
//         flash("ok", "Batch deactivated");
//         store.invalidate(CACHE_KEY);
//         fetchAllBatches();
//     };

//     /* ================= AUDIT ================= */
//     const [auditOpen, setAuditOpen] = useState(false);
//     const [auditRows, setAuditRows] = useState([]);

//     const openAudit = async (epBatchId) => {
//         setAuditOpen(true);
//         setAuditRows([]);

//         try {
//             const res = await fetch(
//                 `${BASE_URL}/api/batches/audits/by-batch?epBatchId=${encodeURIComponent(epBatchId)}`,

//                 { headers: authHeaders }
//             );

//             const text = await res.text();   // ✅ read raw text safely
//             const data = text ? JSON.parse(text) : [];

//             setAuditRows(Array.isArray(data) ? data : []);
//         } catch (err) {
//             console.error("Audit fetch failed", err);
//             setAuditRows([]);
//         }
//     };

//     const downloadTemplate = () => {
//         const a = document.createElement("a");
//         a.href = "/templates/batch_template.xlsx"; // local path
//         a.download = "batch_template.xlsx";
//         document.body.appendChild(a);
//         a.click();
//         a.remove();
//     };



//     /* ================= UI ================= */
//     return (
//         <div className="cmp-page">
//             <h1>Batch Management</h1>

//             {msg.text && (
//                 <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
//                     <span>{msg.text}</span>
//                     <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
//                 </div>
//             )}

//             {uploadProgress && (
//                 <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
//                     <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
//                         <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
//                             {uploadProgress.error
//                                 ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
//                                 : uploading
//                                     ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
//                                     : `Upload complete`}
//                         </span>
//                         <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
//                             {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
//                             &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
//                         </span>
//                     </div>
//                     <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
//                         <div style={{
//                             height: "100%",
//                             width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
//                             background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
//                             borderRadius: 999,
//                             transition: "width 0.3s ease",
//                         }} />
//                     </div>
//                     <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
//                         {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
//                     </div>
//                 </div>
//             )}

//             {/* Upload */}
//             <div className="card grid-4">
//                 <input
//                     type="file"
//                     accept=".xlsx,.xls"
//                     style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }}
//                     onChange={(e) => setFile(e.target.files[0])}
//                 />
//                 <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
//                     {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
//                 </button>
//                 <button className="btn btn-outline" onClick={downloadTemplate}>
//                     Download Excel Template
//                 </button>

//             </div>

//             {/* Table */}
//             <div className="card">


//                 <div className="card-top">
//                     <div className="card-title" style={{ fontWeight: "800" }}>2️⃣ Batch Table</div>

//                     <div className="table-actions" style={{ marginTop: "1rem" }}>
//                         <input
//                             className="search"
//                             placeholder="Search batch..."
//                             style={{ marginRight: "1rem" }}
//                             value={search}
//                             onChange={(e) => setSearch(e.target.value)}
//                         />
//                         <button className="btn" onClick={exportExcel} disabled={exporting}>
//                             {exporting ? "Processing..." : "Export Excel"}
//                         </button>

//                     </div>
//                 </div>
//                 {totalCount !== null && (
//                     <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginTop: 4, marginBottom: 8 }}>
//                         Total: {totalCount.toLocaleString()}
//                     </div>
//                 )}




//                 <div className="table-wrap">
//                     <table className="ep-table">
//                         <thead>
//                             <tr>
//                                 <th>#</th>
//                                 <th>EP Batch ID</th>
//                                 <th>FY</th>
//                                 <th>Batch Creation Date</th>
//                                 <th>Scheme</th>
//                                 <th>Project ID</th>
//                                 <th>Project Implementation Type</th>
//                                 <th>Sector Name</th>
//                                 <th>Job Role Name</th>
//                                 <th>Associated QP Code</th>
//                                 <th>NSQF Level</th>
//                                 <th>Training Hours / Day</th>
//                                 <th>Batch Start Time</th>
//                                 <th>Batch End Time</th>
//                                 <th>Batch Name</th>
//                                 <th>Batch Start Date</th>
//                                 <th>Batch End Date</th>
//                                 <th>Batch Type</th>
//                                 <th>Batch Size</th>
//                                 <th>Assessment Mode</th>
//                                 <th>Proposed Assessment Date</th>
//                                 <th>Training Fee</th>
//                                 <th>Fees Paid By</th>
//                                 <th>Fees Paid By Name</th>
//                                 <th>SIDH Batch ID</th>
//                                 <th>Actions</th>
//                             </tr>
//                         </thead>

//                         <tbody>
//                             {filtered.slice(0, visibleCount).map((b, index) => (
//                                 <tr key={b.epBatchId}>
//                                     <td>{index + 1}</td>
//                                     <td>{b.epBatchId}</td>
//                                     <td>{b.fy}</td>
//                                     <td>{b.batchCreationDate}</td>
//                                     <td>{b.scheme}</td>
//                                     <td>{b.projectId}</td>
//                                     <td>{b.projectImplementationType}</td>
//                                     <td>{b.sectorName}</td>
//                                     <td>{b.jobRoleName}</td>
//                                     <td>{b.associatedQpCode}</td>
//                                     <td>{b.nsqfLevel}</td>
//                                     <td>{b.trainingHoursPerDay}</td>
//                                     <td>{b.batchStartTime}</td>
//                                     <td>{b.batchEndTime}</td>
//                                     <td>{b.batchName}</td>
//                                     <td>{b.batchStartDate}</td>
//                                     <td>{b.batchEndDate}</td>
//                                     <td>{b.batchType}</td>
//                                     <td>{b.batchSize}</td>
//                                     <td>{b.assessmentMode}</td>
//                                     <td>{b.proposedAssessmentDate}</td>
//                                     <td>{b.trainingFee}</td>
//                                     <td>{b.feesPaidBy}</td>
//                                     <td>{b.feesPaidByName}</td>
//                                     <td>{b.sidhBatchId}</td>
//                                     <td className="actions">
//                                         <button className="btn btn-sm" onClick={() => openEdit(b)}>
//                                             Edit
//                                         </button>
//                                         <button
//                                             className="btn btn-sm btn-danger"
//                                             onClick={() => deleteBatch(b.epBatchId)}
//                                         >
//                                             Deactivate
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))}

//                             {!filtered.length && (
//                                 <tr>
//                                     <td colSpan="26" className="muted">
//                                         {loading ? "Loading..." : "No batches found"}
//                                     </td>
//                                 </tr>
//                             )}
//                             {!allLoaded && !loading && (
//                                 <tr>
//                                     <td colSpan="26" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button className="btn btn-outline" onClick={fetchAllBatches} disabled={loadingAll}>
//                                             {loadingAll ? "Loading all data…" : "Load All Data"}
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                             {allLoaded && filtered.length > visibleCount && (
//                                 <tr>
//                                     <td colSpan="26" style={{ textAlign: "center", padding: "14px" }}>
//                                         <button
//                                             className="btn btn-outline"
//                                             onClick={() => setVisibleCount((c) => c + 100)}
//                                         >
//                                             Show More ({filtered.length - visibleCount} remaining)
//                                         </button>
//                                     </td>
//                                 </tr>
//                             )}
//                         </tbody>
//                     </table>

//                 </div>
//             </div>

//             {/* EDIT MODAL */}
//             {editingId &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setEditingId(null)}>
//                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                             <h2>Edit Batch — {editingId}</h2>

//                             <div className="form-grid">
//                                 {[
//                                     { key: "batchName", label: "Batch Name" },
//                                     { key: "batchType", label: "Batch Type" },
//                                     { key: "batchSize", label: "Batch Size" },
//                                     { key: "batchStartDate", label: "Batch Start Date", type: "date" },
//                                     { key: "batchEndDate", label: "Batch End Date", type: "date" },
//                                 ].map(({ key, label, type = "text" }) => (
//                                     <div className="field" key={key}>
//                                         <label>{label}</label>
//                                         <input
//                                             type={type}
//                                             value={form[key] ?? ""}
//                                             onChange={(e) =>
//                                                 setForm((p) => ({ ...p, [key]: e.target.value }))
//                                             }
//                                         />
//                                     </div>
//                                 ))}
//                             </div>

//                             <div className="modal-actions">
//                                 <button className="btn" onClick={() => setEditingId(null)}>
//                                     Cancel
//                                 </button>
//                                 <button className="btn btn-primary" onClick={updateBatch}>
//                                     Save
//                                 </button>
//                             </div>
//                         </div>
//                     </div>,
//                     document.body
//                 )}


//             {/* AUDIT MODAL */}
//             {auditOpen &&
//                 createPortal(
//                     <div className="modal-overlay" onClick={() => setAuditOpen(false)}>
//                         <div className="ep-modal wide" onClick={(e) => e.stopPropagation()}>
//                             <h2>Batch Audit Logs</h2>
//                             {/* <pre>{JSON.stringify(auditRows, null, 2)}</pre> */}
//                             {auditRows.length === 0 ? (
//                                 <div className="muted">No audit logs found</div>
//                             ) : (
//                                 <pre>{JSON.stringify(auditRows, null, 2)}</pre>
//                             )}

//                         </div>
//                     </div>,
//                     document.body
//                 )}

//             {/* ================= INLINE CSS ================= */}
//             <style>{`
//             .form-grid {
//   display: grid;
//   grid-template-columns: repeat(3, 1fr);
//   gap: 12px;
// }

// .field {
//   display: flex;
//   flex-direction: column;
//   gap: 6px;
// }

// .field label {
//   font-size: 11px;
//   font-weight: 700;
//   color: #6b7280;
// }

// .field input {
//   padding: 10px;
//   border-radius: 10px;
//   border: 1px solid #e5e7eb;
// }

// .ep-modal.wide {
//   width: 1100px;
//   max-height: 85vh;
//   overflow: auto;
// }

//         .cmp-page { padding:22px; background:#f7f7fb; min-height:100vh; font-family:Inter }
//         h1 { font-size:26px; font-weight:900 }

//         .flash { padding:10px; border-radius:10px; margin:10px 0; font-weight:700 }
//         .flash-ok { background:#ecfdf5; color:#065f46 }
//         .flash-err { background:#fef2f2; color:#991b1b }

//         .card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; padding:16px; margin-bottom:14px }
//         .grid-4 { display:grid; grid-template-columns:1fr auto auto; gap:10px }

//         .btn { border:1px solid #e5e7eb; padding:10px 14px; border-radius:12px; font-weight:800; cursor:pointer; background:#fff }
//         .btn-primary { background:#2563eb; color:#fff; border-color:#2563eb }
//         .btn-sm { padding:6px 10px; font-size:12px }
//         .btn-danger { color:#991b1b; border-color:#fca5a5 }

//         .search { padding:10px; border-radius:12px; border:1px solid #e5e7eb; margin-bottom:10px }

//         .table-wrap { overflow-x:auto }
//         .ep-table { width:100%; min-width:800px; border-collapse:collapse }
//         th, td { padding:10px; border-bottom:1px solid #e5e7eb; font-size:13px; white-space:nowrap }
//         th { color:#6b7280; text-align:left }

//         .actions { display:flex; gap:8px }
//         .muted { color:#6b7280; font-weight:700 }

//         .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); display:flex; align-items:center; justify-content:center; z-index:99999 }
//         .ep-modal { background:#fff; padding:18px; border-radius:16px; width:520px; max-height:90vh; overflow:auto }
//         .ep-modal.wide { width:900px }
//         .ep-modal input { width:100%; margin-bottom:10px; padding:10px; border-radius:12px; border:1px solid #e5e7eb }

//         .modal-actions { display:flex; justify-content:flex-end; gap:10px; margin-top:10px }
//       `}</style>
//         </div>
//     );
// }
